import { has, range, reduce, reduced, reject } from 'ramda'

import { AccrualCompensation, CompensationSettings } from 'app/dataTypes/subsidy/settings'

import { mustBePair } from '../helpers'


export type CompensationSettingsAPI = {
  shift_1_accrual_customized: boolean, // флаг индивидуальных настроек для первой смены
  shift_1_accrual_on_complexes: Array<number>, // список комплектов для первой смены
  shift_1_accrual_on_sum: null | number,           // сумма для первой смены
  shift_1_accrual_on_sum_1_4: null | number,       // сумма для 1-4 классов
  shift_1_accrual_on_sum_5_11: null | number,      // сумма для 5-11 классов
  shift_1_accrual_type: 'complexes' | 'chosen_complex'  // тип начисления для первой смены
  shift_1_for_certain_complexes: boolean,  // начислять сумму только на комплекты

  shift_2_accrual_customized: boolean, // флаг индивидуальных настроек для второй смены
  shift_2_accrual_on_complexes: Array<number>, // список комплектов для второй смены
  shift_2_accrual_on_sum: null | number,           // сумма для второй смены
  shift_2_accrual_on_sum_1_4: null | number,       // сумма для 1-4 классов
  shift_2_accrual_on_sum_5_11: null | number,      // сумма для 5-11 классов
  shift_2_accrual_type: 'complexes' | 'chosen_complex' // тип начисления для первой смены
  shift_2_for_certain_complexes: boolean,  // начислять сумму только на комплекты


  shift_3_accrual_customized: boolean, // флаг индивидуальных настроек для третьей смены
  shift_3_accrual_on_complexes: Array<number>, // список комплектов для третьей смены
  shift_3_accrual_on_sum: null | number, // сумма для третьей смены
  shift_3_accrual_on_sum_1_4: null | number,       // сумма для 1-4 классов
  shift_3_accrual_on_sum_5_11: null | number,      // сумма для 5-11 классов
  shift_3_accrual_type: 'complexes' | 'chosen_complex'  // тип начисления для первой смены
  shift_3_for_certain_complexes: boolean,  // начислять сумму только на комплекты

  for_single_sales_enabled: boolean, // флаг отображения начисления льгот при личных продажах
  for_single_sales_checked: boolean, // флаг принудительного выбора начисления льгот при личных продажах
  for_klass_orders_enabled: boolean, // флаг отображения начисления льгот при продажах в классе
  for_klass_orders_checked: boolean, // флаг принудительного выбора начисления льгот при продажах в классе

  include_saturday_enabled: boolean, // флаг отображения начисления льгот при продажах по субботам
  include_saturday_checked: boolean, // флаг принудительного выбора начисления льгот при продажах по субботам

  homeschooling_only: boolean,              // только для домашнего обучения
  accrual_period: 'month' | 'quarter'               // период начисления  
}

export const MAX_SHIFTS = 3

export const convertAsCompensation = (
  settings: CompensationSettingsAPI,
  complexesMap: Map<number, string>,
  common: boolean,
): CompensationSettings => {
  const accruals = reduce((acc, val) => {
    if (!has(`shift_${val}_accrual_customized`, settings)) {
      reduced(acc)
    }
    const active: boolean = settings[`shift_${val}_accrual_customized`]
    const complexesIDs: Array<number> = settings[`shift_${val}_accrual_on_complexes`]
    const sum: null | number = parseFloat(settings[`shift_${val}_accrual_on_sum`])
    const sum1_4: null | number = parseFloat(settings[`shift_${val}_accrual_sum_1_4`])
    const sum5_11: null | number = parseFloat(settings[`shift_${val}_accrual_sum_5_11`])
    const type: 'complexes' | 'chosen_complex' = settings[`shift_${val}_accrual_type`]
    const forCertainComplexes: boolean = settings[`shift_${val}_for_certain_complexes`]

    acc.push({
      num: val,
      active,
      complexes: complexesIDs.map(key => ({
        ID: String(key),
        title: complexesMap.get(key) || String(key),
      })),
      sum: typeof sum === 'number' && !Number.isNaN(sum) ? String(sum) : '',
      sum1_4: typeof sum1_4 === 'number' && !Number.isNaN(sum1_4) ? String(sum1_4) : '',
      sum5_11: typeof sum5_11 === 'number' && !Number.isNaN(sum5_11) ? String(sum5_11) : '',
      type,
      forCertainComplexes
    })
    return acc
  }, [] as Array<AccrualCompensation>, range(1, MAX_SHIFTS + 1))


  const accrualsFor = {
    single: mustBePair([settings.for_single_sales_enabled, settings.for_single_sales_checked]),
    class: mustBePair([settings.for_klass_orders_enabled, settings.for_klass_orders_checked]),
  }

  const include = {
    saturdays: mustBePair([settings.include_saturday_enabled, settings.include_saturday_checked]),
  }

  return reject(value => (value === undefined), {
    app: 'compensation' as const,
    accrualsShiftsAmount: MAX_SHIFTS,
    accruals,
    accrualsFor,
    include,
    homeschoolingOnly: settings.homeschooling_only,
    accrualPeriod: settings.accrual_period
  })
}

export const convertAsCompensationPOST = (settings: CompensationSettings): CompensationSettingsAPI => {
  const {
    accruals,
    accrualsFor,
    include,
    homeschoolingOnly,
    accrualPeriod
  } = settings

  const settingsAccruals = reduce((acc, {
    num,
    active,
    complexes,
    sum,
    sum1_4,
    sum5_11,
    type,
    forCertainComplexes
  }) => {
    acc[`shift_${num}_accrual_customized`] = active
    acc[`shift_${num}_accrual_on_complexes`] = complexes.map(({ ID }) => parseInt(ID, 10))
    acc[`shift_${num}_accrual_on_sum`] = sum ? parseFloat(sum) : null
    acc[`shift_${num}_accrual_on_sum_1_4`] = sum1_4 ? parseFloat(sum1_4) : null
    acc[`shift_${num}_accrual_on_sum_5_11`] = sum5_11 ? parseFloat(sum5_11) : null
    acc[`shift_${num}_accrual_type`] = type
    acc[`shift_${num}_for_certain_complexes`] = forCertainComplexes
    return acc
  }, {} as CompensationSettingsAPI, accruals)

  const settingsAccrualsFor = {
    for_single_sales_enabled: accrualsFor.single[0],
    for_single_sales_checked: accrualsFor.single[1],
    for_klass_orders_enabled: accrualsFor.class[0],
    for_klass_orders_checked: accrualsFor.class[1],
  }

  const settingsInclude = {
    include_saturday_enabled: include.saturdays[0],
    include_saturday_checked: include.saturdays[1],
  }

  return {
    ...settingsAccruals,
    ...settingsAccrualsFor,
    ...settingsInclude,
    homeschooling_only: homeschoolingOnly,
    accrual_period: accrualPeriod
  }
}
